import agent from 'superagent';
import get from 'lodash/get';
import * as Sentry from '@sentry/browser';

import { prepareErrorPayload, findUserId } from './prepareErrorPayload';

export function reportError(message, error) {
    if (isLocalEnvironment()) return;

    // Report it to our server
    sendToMONNI(message, error);

    if (error) {
        // Report it to Sentry
        Sentry.captureException(error);
    }
}

// NaN shouldn't happen, but just in case one slips to production we send an error to Sentry
// to reproduce and fix it
export const NaNReporter = () => {
    // let's wait for a while to make sure NaN is not eventually fixed after API calls finish
    const twoSeconds = 2000;
    window.setTimeout(() => {
        const mainAppContainer = document.getElementById('root');
        if (!mainAppContainer) return;
        const fullPageText = mainAppContainer.innerText || '';

        const FirstNaNPosition = fullPageText.indexOf('NaN');
        if (FirstNaNPosition > -1) {
            // "NaN" was found, clip the string before and after its first occurrence
            const clippedNaNTextOnPage = fullPageText.slice(
                FirstNaNPosition - 40,
                FirstNaNPosition + 40,
            );

            // just to include how many times it was found
            const totalFound = fullPageText.match(/NaN/g).length;

            console.error(
                `YS NaN was displayed ${totalFound} time(s): `,
                clippedNaNTextOnPage,
            );

            reportError(
                `NaN was displayed ${totalFound} time(s)`,
                Error(
                    `NaN was displayed ${totalFound} time(s): ${clippedNaNTextOnPage}`,
                ),
            );
        }
    }, twoSeconds);
};

// Sends errors to our internal error tracking system
function sendToMONNI(message, error) {
    if (!window.yconfig.MONNI || isLocalEnvironment()) return;
    const { url: errorReportingEndpoint, _id, key } = window.yconfig.MONNI;

    // errorReporting settings not found
    if (!errorReportingEndpoint || !_id || !key) return;

    // Send error to MONNI
    agent
        .post(errorReportingEndpoint)
        .set('Content-Type', 'application/json')
        .send(prepareErrorPayload(message, error))
        .auth(_id, key)
        .catch(error => {
            console.error('Reporting errors failed: ', error);
        });
}

function isLocalEnvironment() {
    return get(window, 'yconfig.env') === 'local';
}

// initialize Sentry, the error reporting service
export function startSentry() {
    if (isLocalEnvironment()) return;
    const SentryDSN = get(window, 'yconfig.SentryDSN');

    if (!SentryDSN) return;
    Sentry.init({
        dsn: SentryDSN,
        environment: window.location.hostname,
        release: 'account-ui_' + process.env.REACT_APP_GIT_SHA,
        ignoreErrors: [
            // Random plugins/extensions
            'top.GLOBALS',
            // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
            'originalCreateNotification',
            'canvas.contentDocument',
            'MyApp_RemoveAllHighlights',
            'http://tt.epicplay.com',
            "Can't find variable: ZiteReader",
            'jigsaw is not defined',
            'ComboSearch is not defined',
            'http://loading.retry.widdit.com/',
            'atomicFindClose',
            // Facebook borked
            'fb_xd_fragment',
            // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
            // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
            'bmi_SafeAddOnload',
            'EBCallBackMessageReceived',
            // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
            'conduitPage',
            // Generic error code from errors outside the security sandbox
            // You can delete this if using raven.js > 1.0, which ignores these automatically.
            'Script error.',
        ],
        ignoreUrls: [
            // Facebook flakiness
            /graph\.facebook\.com/i,
            // Facebook blocked
            /connect\.facebook\.net\/en_US\/all\.js/i,
            // Woopra flakiness
            /eatdifferent\.com\.woopra-ns\.com/i,
            /static\.woopra\.com\/js\/woopra\.js/i,
            // Chrome extensions
            /extensions\//i,
            /^chrome:\/\//i,
            // Other plugins
            /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
            /webappstoolbarba\.texthelp\.com\//i,
            /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
            // our local dev
            /127\.0\.0.1:3000/,
            /localhost/,
        ],
    });

    const userId = findUserId();

    if (!userId) return;
    Sentry.configureScope(function(scope) {
        // add user id to sentry events
        scope.setUser({ id: userId });
    });
}
